import { Box } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { imageColumn, imageTableColumn, styleImageColumn } from "data/globalData";
import { trimCategoryDropdownsObj } from "components/PimDashboard/DashboardComponents/Sourcing/TrimsMaster/data";
import {
	breakdownGridRowIdKey,
	breakdownRowUpdatedKey,
	isTruthyBreakdownLevel,
	normalizeTrimBreakdownRowsForGrid,
	reqColorLevelColsForTrimBreakdown,
	trimBreakdownTableColumns,
	getTrimItemBreakdownRows,
	reconcileTrimBreakdownRows,
	pimTrimBreakdownAssociationObj as trimBreakdownAssociationKey,
} from "../data";
import { isCatalogDrivenTrimCategory } from "../trimCatalogHelpers";

export { trimBreakdownAssociationKey };

const colStyle = { headerAlign: "center", align: "center" };

export const toGridColumn = ({ field, headerName, flex = 1, minWidth = 120, renderCell, ...rest }) => ({
	field,
	headerName,
	flex,
	minWidth,
	...colStyle,
	...(renderCell ? { renderCell } : {}),
	...rest,
});

export const buildLevelColumns = ({ allAttributes }) => {
	const allOrValue = (value) => value || "ALL";
	const emptyOrValue = (value) => value || "—";

	const colorLevelColumns =
		allAttributes
			?.filter((row) => reqColorLevelColsForTrimBreakdown.includes(row?.key))
			.map((row) =>
				toGridColumn({
					field: row.key,
					headerName: row.name,
					flex: 0.9,
					minWidth: 110,
					renderCell: ({ value }) =>
						row.key === "color" ? allOrValue(value) : emptyOrValue(value),
				}),
			) ?? [];

	const sizeLevelColumns = [
		toGridColumn({
			field: trimBreakdownTableColumns.size.key,
			headerName: trimBreakdownTableColumns.size.name,
			flex: 0.8,
			minWidth: 100,
			renderCell: ({ value }) => allOrValue(value),
		}),
	];

	const channelLevelColumns = [
		toGridColumn({
			field: trimBreakdownTableColumns.channel.key,
			headerName: trimBreakdownTableColumns.channel.name,
			flex: 0.9,
			minWidth: 110,
			renderCell: ({ value }) => allOrValue(value),
		}),
	];

	return [...colorLevelColumns, ...sizeLevelColumns, ...channelLevelColumns];
};

export const buildBaseColumns = () => [
	toGridColumn({
		field: trimBreakdownTableColumns.item_category.key,
		headerName: trimBreakdownTableColumns.item_category.name,
		flex: 1,
		minWidth: 120,
	}),
	toGridColumn({
		field: trimBreakdownTableColumns.item_sub_category.key,
		headerName: trimBreakdownTableColumns.item_sub_category.name.trim(),
		flex: 1.15,
		minWidth: 140,
	}),
];

export const buildImageColumn = () => [
	{
		...imageTableColumn(styleImageColumn),
		field: imageColumn.key,
		flex: 0.75,
		minWidth: 90,
		valueGetter: (_value, row) => row?.[imageColumn.key] ?? row?.url ?? "",
	},
];

export const getTrimCodeDisplayValue = (rowOrValue, trimCodeKey = trimBreakdownTableColumns.trim_code.key) => {
	if (rowOrValue == null || rowOrValue === "" || rowOrValue === "SELECT") return "";
	if (typeof rowOrValue === "object") {
		return rowOrValue?.trim_code ?? rowOrValue?.key ?? rowOrValue?.name ?? "";
	}
	if (typeof rowOrValue === "string") return rowOrValue;
	return String(rowOrValue?.[trimCodeKey] ?? "");
};

export const buildTrimMasterSearchData = (row, trimCodeKey = trimBreakdownTableColumns.trim_code.key) => {
	const trimCode = getTrimCodeDisplayValue(row?.[trimCodeKey]);
	if (!trimCode) {
		return { [trimCodeKey]: null };
	}
	const itemDescription = row?.[trimBreakdownTableColumns.description.key] ?? row?.item_description ?? "";
	return {
		[trimCodeKey]: {
			key: trimCode,
			trim_code: trimCode,
			item_description: itemDescription,
			url: row?.[imageColumn.key] ?? row?.url ?? "",
			name: itemDescription ? `${trimCode} - ${itemDescription}` : trimCode,
		},
	};
};

export const renderTrimCodeDisplayCell = ({ row, value, trimCodeKey = trimBreakdownTableColumns.trim_code.key } = {}) => {
	const text = getTrimCodeDisplayValue(value ?? row?.[trimCodeKey]) || "—";
	return (
		<Box
			component="span"
			sx={{
				px: 1.25,
				fontSize: "0.8125rem",
				fontWeight: text === "—" ? 400 : 600,
				color: text === "—" ? grey[500] : grey[900],
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				display: "block",
				width: "100%",
			}}
			title={text}>
			{text}
		</Box>
	);
};

const renderDescriptionCell = ({ row, value }) => {
	const text = value ?? row?.[trimBreakdownTableColumns.description.key] ?? row?.item_description ?? row?.description ?? "—";
	return (
		<Box
			component="span"
			sx={{
				px: 1.25,
				fontSize: "0.8125rem",
				color: text === "—" ? grey[500] : grey[800],
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				display: "block",
				width: "100%",
			}}
			title={text}>
			{text}
		</Box>
	);
};

export const buildCommonDetailColumns = () => [
	toGridColumn({
		field: trimBreakdownTableColumns.trim_code.key,
		headerName: trimBreakdownTableColumns.trim_code.name,
		flex: 1.1,
		minWidth: 160,
		renderCell: renderTrimCodeDisplayCell,
	}),
	toGridColumn({
		field: trimBreakdownTableColumns.description.key,
		headerName: trimBreakdownTableColumns.description.name,
		flex: 1.8,
		minWidth: 260,
		valueGetter: (_value, row) =>
			row?.[trimBreakdownTableColumns.description.key] ?? row?.item_description ?? row?.description ?? "",
		renderCell: renderDescriptionCell,
	}),
];

export const buildMasterDetailColumns = ({ renderTrimCodeCell } = {}) => [
	...buildImageColumn(),
	toGridColumn({
		field: trimBreakdownTableColumns.trim_code.key,
		headerName: trimBreakdownTableColumns.trim_code.name,
		flex: 1.3,
		minWidth: 220,
		...(renderTrimCodeCell
			? { renderCell: renderTrimCodeCell }
			: { renderCell: renderTrimCodeDisplayCell }),
	}),
	toGridColumn({
		field: trimBreakdownTableColumns.description.key,
		headerName: trimBreakdownTableColumns.description.name,
		flex: 1.8,
		minWidth: 260,
		valueGetter: (_value, row) =>
			row?.[trimBreakdownTableColumns.description.key] ?? row?.item_description ?? row?.description ?? "",
		renderCell: renderDescriptionCell,
	}),
];

export const buildTrimBreakdownGridColumns = ({ allAttributes, categoryType = "MASTER", renderTrimCodeCell } = {}) => {
	const detailColumns =
		categoryType === "MASTER"
			? buildMasterDetailColumns({ renderTrimCodeCell })
			: [...buildImageColumn(), ...buildCommonDetailColumns()];

	return [...buildBaseColumns(), ...buildLevelColumns({ allAttributes }), ...detailColumns];
};

export const getBreakdownCategoryType = (selectedTrim) => {
	const category = selectedTrim?.item_category;
	if (category === trimCategoryDropdownsObj?.ZIPPER?.key) return "ZIPPER";
	if (category === trimCategoryDropdownsObj?.THREAD?.key) return "THREAD";
	return "MASTER";
};

export const trimBreakdownModalPaperSx = {
	borderRadius: 3,
	overflow: "hidden",
	border: `1px solid ${grey[200]}`,
	boxShadow: "0 24px 48px rgba(15, 23, 42, 0.12)",
	backgroundColor: "#fff",
	maxHeight: "92vh",
	width: "96vw",
	maxWidth: 1600,
	display: "flex",
	flexDirection: "column",
};

export const trimBreakdownHeaderSx = {
	background: `linear-gradient(135deg, ${grey[50]} 0%, #ffffff 70%, ${blue[50]} 100%)`,
	borderBottom: `1px solid ${grey[200]}`,
	px: { xs: 2, sm: 3 },
	py: 2.25,
	flexShrink: 0,
};

export const trimBreakdownBodySx = {
	flex: 1,
	minHeight: 0,
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
	boxSizing: "border-box",
	backgroundColor: grey[50],
};

export const trimBreakdownGridPanelSx = {
	width: "100%",
	minWidth: 0,
	minHeight: 0,
	flex: 1,
	border: `1px solid ${grey[200]}`,
	borderRadius: 2.5,
	overflow: "hidden",
	backgroundColor: "#fff",
	boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
};

export const trimBreakdownEditorSx = {
	mb: 1.5,
	flexShrink: 0,
	p: { xs: 1.5, sm: 2 },
	border: `1px solid ${grey[200]}`,
	borderRadius: 2.5,
	backgroundColor: "#fff",
	boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
};

export const trimBreakdownEditorTitleSx = {
	mb: 1.5,
	fontWeight: 600,
	color: grey[800],
	fontSize: "0.875rem",
};

export const trimBreakdownSelectionChipSx = {
	fontWeight: 600,
	height: 28,
	borderRadius: 999,
	bgcolor: "#fff",
	color: grey[800],
	border: `1px solid ${grey[300]}`,
};

export const trimBreakdownCategoryChipSx = {
	fontWeight: 700,
	height: 28,
	borderRadius: 999,
	letterSpacing: "0.04em",
	fontSize: "0.6875rem",
};

export const trimBreakdownCloseButtonSx = {
	textTransform: "none",
	fontWeight: 700,
	borderRadius: 2,
	px: 2.5,
	borderColor: grey[300],
	color: grey[800],
	"&:hover": {
		borderColor: grey[400],
		backgroundColor: grey[50],
	},
};

export const trimBreakdownGridHeight = (rowCount, { compact = false } = {}) => {
	const maxHeight = compact ? 280 : 360;
	const minHeight = compact ? 160 : 200;
	return Math.min(Math.max(rowCount * 52 + 56, minHeight), maxHeight);
};

export const trimBreakdownSelectedRowSx = {
	backgroundColor: `${blue[50]} !important`,
	boxShadow: `inset 3px 0 0 ${blue[400]}`,
};

export const trimBreakdownBulkSearchInputSx = {
	"& .MuiAutocomplete-inputRoot": {
		minHeight: 40,
		borderRadius: 1.5,
		backgroundColor: "#fff",
	},
	"& .MuiInputBase-root": { fontSize: "0.875rem" },
	"& .MuiOutlinedInput-notchedOutline": { borderColor: grey[300] },
};

export const trimCodeInlineInputSx = {
	"& .MuiStack-root": { height: "100%" },
	"& .MuiAutocomplete-root": { height: "100%" },
	"& .MuiAutocomplete-inputRoot": {
		height: "100%",
		minHeight: 0,
		borderRadius: 1.5,
		backgroundColor: "#fff",
	},
	"& .MuiInputBase-root": { height: "100%", fontSize: "0.8125rem" },
	"& .MuiOutlinedInput-notchedOutline": { borderColor: grey[300] },
};

export const DEFAULT_BREAKDOWN_LEVEL_VALUES = {
	color: "ALL",
	pallete: "",
	option_id: "",
	size: "ALL",
	channel: "ALL",
};

export const ensureBreakdownLevelValues = (row = {}) => ({
	...row,
	color: row.color ?? DEFAULT_BREAKDOWN_LEVEL_VALUES.color,
	pallete: row.pallete ?? DEFAULT_BREAKDOWN_LEVEL_VALUES.pallete,
	option_id: row.option_id ?? DEFAULT_BREAKDOWN_LEVEL_VALUES.option_id,
	[trimBreakdownTableColumns.size.key]: row[trimBreakdownTableColumns.size.key] ?? DEFAULT_BREAKDOWN_LEVEL_VALUES.size,
	[trimBreakdownTableColumns.channel.key]:
		row[trimBreakdownTableColumns.channel.key] ?? DEFAULT_BREAKDOWN_LEVEL_VALUES.channel,
});

export const syncBreakdownRows = (rows) =>
	normalizeTrimBreakdownRowsForGrid(rows.map(ensureBreakdownLevelValues), { prefix: "trim-bd" });

export const getRowKey = (row) => row?.[breakdownGridRowIdKey];

export const matchesBreakdownRowId = (row, gridId) => {
	if (gridId == null || gridId === "") return false;
	const rowGridId = row?.[breakdownGridRowIdKey];
	if (rowGridId != null && String(rowGridId) === String(gridId)) return true;
	if (row?.id != null && String(row.id) === String(gridId)) return true;
	return false;
};

export const trimNeedsBreakdownPanel = (selectedTrim) =>
	isCatalogDrivenTrimCategory(selectedTrim?.item_category) ||
	isTruthyBreakdownLevel(selectedTrim?.color_level) ||
	isTruthyBreakdownLevel(selectedTrim?.size_level) ||
	isTruthyBreakdownLevel(selectedTrim?.channel_level);

export const loadBreakdownRowsFromTrim = (selectedTrim, { colorRows = [], sizeRows = [] } = {}) => {
	const reconciled = reconcileTrimBreakdownRows({
		trimObj: selectedTrim,
		colorRows,
		sizeRows,
		existingRows: getTrimItemBreakdownRows(selectedTrim),
	});
	return syncBreakdownRows(reconciled);
};

export const patchBreakdownRows = ({ rows, targetKeys, patch, userEmail }) => {
	const keySet = new Set(targetKeys.map((key) => String(key)));
	return syncBreakdownRows(
		rows.map((row) => {
			if (!keySet.has(String(getRowKey(row) ?? ""))) return row;
			return {
				...row,
				...patch,
				...(row?.id ? { [breakdownRowUpdatedKey]: true } : {}),
				last_updated_by: userEmail,
			};
		}),
	);
};
